import React, { useState, useEffect, useContext, useRef } from 'react';
import SearchFlightPanel from './SearchFlightPanel';
import './Flight.css';
import { LandingContext } from '../Profile/LandingContext';

const useInView = (options) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setInView(true); observer.unobserve(entry.target); }
        }, options);
        if (ref.current) observer.observe(ref.current);
        return () => { if (ref.current) observer.unobserve(ref.current); };
    }, [ref, options]);
    return [ref, inView];
};

const FlightSearch = () => {
    const landingData = useContext(LandingContext);
    const [offersRef, offersInView]           = useInView({ threshold: 0.1 });
    const [destinationsRef, destinationsInView] = useInView({ threshold: 0.1 });
    const [whyUsRef, whyUsInView]             = useInView({ threshold: 0.1 });

    const flightDeals = [
        { airline: 'IndiGo',    logoUrl: '/assets/images/airlinelogos/6E.png',  title: 'Domestic Flights Bonanza',   description: 'Fly to your favourite domestic destinations at unbeatable fares.', discount: 'Up to 25% OFF', type: 'percent' },
        { airline: 'Vistara',   logoUrl: '/assets/images/airlinelogos/UK.png',  title: 'International Getaways',     description: 'Explore the world with premium cabin comfort and great value.',    discount: 'Flat ₹5,000 OFF', type: 'flat' },
        { airline: 'Air India', logoUrl: '/assets/images/airlinelogos/AI.png',  title: 'Last-Minute Escapes',        description: 'Spontaneous trips at jaw-dropping prices — book today.',           discount: 'From ₹2,999',     type: 'price' },
    ];

    const popularDestinations = [
        { name: 'Paris, France',   image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=2073' },
        { name: 'Dubai, UAE',      image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2070' },
        { name: 'Kyoto, Japan',    image: 'https://images.unsplash.com/photo-1542051841857-5f90071e7989?q=80&w=2070' },
        { name: 'New York, USA',   image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?q=80&w=2070' },
    ];

    const whyChooseUs = [
        { icon: 'ri-price-tag-3-line',        title: 'Best Price Guarantee',  text: 'We offer the most competitive fares across 300+ airlines worldwide.' },
        { icon: 'ri-shield-check-line',        title: 'Secure Payments',       text: 'Your payment is protected with bank-grade encryption.' },
        { icon: 'ri-customer-service-2-line',  title: '24/7 Support',          text: 'Our travel experts are always on hand — any time, any timezone.' },
    ];

    const heroStyle = {
        backgroundImage: landingData?.flightBackgroundSrc
            ? `url(${landingData.flightBackgroundSrc})`
            : `url(/assets/images/flightsearch.png)`,
    };

    return (
        <>
            {/* ── Hero ───────────────────────────────────── */}
            <div className="hero-section" style={heroStyle}>
                <div className="hero-grid-container">
                    <div className="hero-text-content">
                        <h1 className="hero-heading">Search &amp; Book Flights</h1>
                        <p className="hero-subtitle">Discover the best fares to anywhere in the world</p>
                    </div>
                    <div className="hero-form-content">
                        <SearchFlightPanel />
                    </div>
                </div>
            </div>

            {/* ── Body ───────────────────────────────────── */}
            <div className="page-content" style={{ paddingTop: 0 }}>
                <div className="container py-5">

                    {/* Offers */}
                    <section ref={offersRef} className={`py-5 section-container${offersInView ? ' is-visible' : ''}`}>
                        <h2 className="section-title">Exclusive Flight Offers</h2>
                        <div className="row g-4">
                            {flightDeals.map((deal, i) => (
                                <div className="col-lg-4 col-md-6" key={i}>
                                    <div className={`offer-card offer-card-${deal.type}`}>
                                        <div className="offer-card-header">
                                            <img src={deal.logoUrl} alt={deal.airline} className="airline-logo" />
                                            <span className="discount-badge">{deal.discount}</span>
                                        </div>
                                        <div className="offer-card-body">
                                            <h5 className="card-title">{deal.title}</h5>
                                            <p className="card-text">{deal.description}</p>
                                            <a href="#!" className="stretched-link">
                                                Book Now <i className="ri-arrow-right-line" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Popular destinations */}
                    <section ref={destinationsRef} className={`py-5 section-container${destinationsInView ? ' is-visible' : ''}`}>
                        <h2 className="section-title">Popular Flight Destinations</h2>
                        <div className="row g-4">
                            {popularDestinations.map((dest, i) => (
                                <div className="col-lg-3 col-md-6" key={i}>
                                    <a href="#!" className="destination-card-modern">
                                        <img src={dest.image} alt={dest.name} className="card-img" />
                                        <div className="card-img-overlay">
                                            <h4 className="card-title">{dest.name}</h4>
                                        </div>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Why us */}
                    <section ref={whyUsRef} className={`py-5 section-container why-us-section${whyUsInView ? ' is-visible' : ''}`}>
                        <h2 className="section-title">Why Book With Us?</h2>
                        <div className="row g-4">
                            {whyChooseUs.map((item, i) => (
                                <div className="col-lg-4 col-md-6" key={i}>
                                    <div className="why-us-item">
                                        <div className="why-us-icon"><i className={item.icon} /></div>
                                        <div className="why-us-content">
                                            <h5 className="title">{item.title}</h5>
                                            <p className="text">{item.text}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                </div>
            </div>
        </>
    );
};

export default FlightSearch;
